import React from 'react';
import {Link} from 'react-router-dom';
import {AdminDashboardStats} from '../../domain/admin';
import {formatCount} from './AdminPrimitives';
import {AlertTriangleIcon, ArrowRightIcon, MailIcon} from '../Icons';

/**
 * The dashboard tiles.
 *
 * Two groups, not one grid of eight: the counts that describe the business, and the counts that
 * mean somebody is stuck. `signups_failed` is the whole reason the second group exists — every one
 * of those is a person who has been charged and cannot sign in — so it is a full-width red panel
 * with the fix one click away, not the sixth tile in a row of grey numbers.
 */

/** Matches the window `users_new_30d` counts, so the tile and the table it opens agree. */
const thirtyDaysAgoIso = (): string =>
  new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();

interface TileProps {
  label: string;
  /** Not every dashboard field has landed on every response yet — treat it as possibly missing. */
  value: number | null | undefined;
  hint?: string;
  to?: string;
}

const Tile: React.FC<TileProps> = ({label, value, hint, to}) => {
  const body = (
    <>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums text-gray-50">{formatCount(value)}</p>
      {hint && <p className="mt-1 text-[11px] text-gray-600">{hint}</p>}
    </>
  );

  if (to) {
    return (
      <Link
        to={to}
        className="rounded-xl border border-gray-800 bg-gray-900/60 px-4 py-3 transition-colors
         hover:border-gray-700 hover:bg-gray-900"
      >
        {body}
      </Link>
    );
  }

  return <div className="rounded-xl border border-gray-800 bg-gray-900/60 px-4 py-3">{body}</div>;
};

interface AdminStatTilesProps {
  stats: AdminDashboardStats;
}

const AdminStatTiles: React.FC<AdminStatTilesProps> = ({stats}) => {
  // Every field below is a genuine `number` in the type, but the endpoint is still landing fields
  // one at a time — treat each as possibly missing rather than trust the contract at render time.
  const signupsFailed = stats.signupsFailed ?? 0;
  const suppressionsActive = stats.suppressionsActive ?? 0;
  const emailsUndelivered30d = stats.emailsUndelivered30d ?? 0;

  return (
    <div className="space-y-4">
      {/*
      The alert, first and loudest. `signups_failed` counts paid checkouts with no account behind
      them, so it is never "just a number that is non-zero" — it is a queue of people to unblock.
    */}
      {signupsFailed > 0 && (
        <div className="rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-start gap-2.5">
              <AlertTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-red-400" />
              <div>
                <p className="text-sm font-semibold text-red-100">
                  {signupsFailed} paid {signupsFailed === 1 ? 'signup has' : 'signups have'}
                  {' '}no account
                </p>
                <p className="mt-0.5 text-xs text-red-200/80">
                These people have been charged and cannot sign in. Provisioning is retried by cron,
                so a signup that is still here has failed more than once.
                </p>
              </div>
            </div>
            <Link
              to="/admin/signups?status=failed"
              className="inline-flex items-center gap-1.5 rounded-lg bg-red-500/20 px-3 py-1.5 text-xs
             font-medium text-red-100 transition-colors hover:bg-red-500/30"
            >
            Open the queue
              <ArrowRightIcon className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      )}

      {/*
      A suppressed address swallows every future send silently, so it is an alert even though
      nothing has visibly failed yet. Amber rather than red: it costs nobody money, it just makes
      the setup email — the one thing this panel can send — quietly useless.
    */}
      {suppressionsActive > 0 && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-start gap-2.5">
              <MailIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
              <div>
                <p className="text-sm font-semibold text-amber-100">
                  {suppressionsActive}{' '}
                  {suppressionsActive === 1 ? 'address is' : 'addresses are'} suppressed
                </p>
                <p className="mt-0.5 text-xs text-amber-200/80">
                Resend accepts sends to these and delivers nothing. Anyone on this list cannot
                receive a setup email until it is cleared in the Resend dashboard.
                </p>
              </div>
            </div>
            <Link
              to="/admin/emails?tab=suppressions"
              className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500/20 px-3 py-1.5 text-xs
             font-medium text-amber-100 transition-colors hover:bg-amber-500/30"
            >
            See the list
              <ArrowRightIcon className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Tile label="Users" value={stats.usersTotal} to="/admin/users" />
        <Tile
          label="Active subscribers"
          value={stats.subscribersActive}
          hint="Derived, not the raw flag"
          to="/admin/users?status=active"
        />
        <Tile
          label="Paying subscribers"
          value={stats.subscribersPaid}
          hint="Subscribed on a plan above free"
        />
        <Tile
          label="New in 30 days"
          value={stats.usersNew30d}
          hint="Exactly these rows"
        // `created_after` makes the tile and the table it opens agree on the same number, which a
        // sort alone could not do.
          to={`/admin/users?created_after=${thirtyDaysAgoIso()}&sort=created_at&order=desc`}
        />
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Tile
          label="Transcriptions in flight"
          value={stats.transcriptionsInFlight}
          hint="Running right now — oldest is the stuck one"
          to="/admin/queue?status=processing"
        />
        <Tile
          label="Open signups"
          value={stats.signupsOpen}
          hint="Started, not yet provisioned"
          to="/admin/signups?status=pending"
        />
        <Tile
          label="Failed signups"
          value={stats.signupsFailed}
          hint="Paid, no account"
          to="/admin/signups?status=failed"
        />
        <Link
          to="/admin/emails?status=undelivered"
          className="rounded-xl border border-gray-800 bg-gray-900/60 px-4 py-3 transition-colors
         hover:border-gray-700 hover:bg-gray-900"
          title="Bounced, marked as spam, suppressed, or the send itself failed. Delivery statuses only advance once the Resend webhook is registered in this environment."
        >
          <p className="flex items-center gap-1.5 text-xs text-gray-500">
            <MailIcon className="h-3.5 w-3.5" />
          Undelivered email (30d)
          </p>
          <p
            className={`mt-1 text-2xl font-semibold tabular-nums ${
              emailsUndelivered30d > 0 ? 'text-amber-300' : 'text-gray-50'
            }`}
          >
            {formatCount(stats.emailsUndelivered30d)}
          </p>
          <p className="mt-1 text-[11px] text-gray-600">Individual sends that did not arrive.</p>
        </Link>
      </div>
    </div>
  );
};

export default AdminStatTiles;
